/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { useDocumentEventHandler } from 'src/core/hooks/useDocumentEventHandler';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { routeTable } from 'src/core/utils/routeTable';
import { getAllTreeNodes } from 'src/core/utils/tree';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { PrebuiltEntitiesModal } from 'src/luis/modules/entities/components/prebuiltEntitiesModal/PrebuiltEntitiesModal';
import { EntityDropdown } from 'src/luis/modules/labeling/components/EntityDropdown';
import { EntityDropdownProps, EntityDropdownSelection } from 'src/luis/modules/labeling/types/EntityDropdownProps';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { ListEntity } from 'src/luis/projects/entities/types/ListEntity';
import { getAbsoluteParentById } from 'src/luis/projects/entities/utils/simpleEntityHelpers/simpleEntityHelpers';
import { TokenSelectionMetadata } from 'src/luis/projects/utterances/stores/LabelingStore';
import { MenuStore } from 'src/luis/projects/utterances/stores/MenuStore';
import { isMLPrediction } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityLabelStore } from 'src/luis/types/stores/utterances/EntityLabelStore';
import { Label } from 'src/modules/labeling/types/Label';
import { tokenDataAttribute } from 'src/modules/labeling/utils/labelingConstants';

export type EntityDropdownContainerProps = {
    /**
     * The menu store that encapsulate all the menu UI states.
     */
    menuStore: MenuStore;

    /**
     * The token selection metadata object.
     */
    tokenSelectionMetadata: TokenSelectionMetadata;

    /**
     * Labels of the selected utterance.
     */
    utteranceLabels: Label[];

    /**
     * Callback to set the token selection metadata object.
     */
    onSetTokenSelectionMetadata: (obj: TokenSelectionMetadata) => void;

    /**
     * A function that triggers opening entity palette.
     */
    onOpenEntityPane: () => void;

    /**
     * Array of entities.
     */
    entities: Entity[];

    /**
     * Determines whether the entity pane is opened or not.
     */
    isEntityPaneOpen: boolean;

    /**
     * Entity dropdown actions.
     */
    actions: {
        onCreateEntity: (entityName: string) => void;
        onAddLabel: (
            id: LuisId,
            label: Pick<EntityLabelStore, 'entityId' | 'roleId' | 'startTokenIndex' | 'endTokenIndex'>,
            method: 'brush' | 'menu' | 'createNewEntity'
        ) => void;
        onRemoveLabels: (id: LuisId, labels: Pick<EntityLabelStore, 'entityId' | 'roleId' | 'startTokenIndex' | 'endTokenIndex'>[]) => void;
        onCreateSublist: (entityId: LuisId, sublistName: string, values: string[]) => void;
        onAddSynonymToSublist: (entityId: LuisId, sublistName: string, value: string) => void;
        onConfirmPrediction: (
            id: LuisId,
            labels: Pick<EntityLabelStore, 'entityId' | 'roleId' | 'startTokenIndex' | 'endTokenIndex'>[],
            method: 'predictionsMenu' | 'predictionsMenuIncludeSubEntities' | 'selectionMenu'
        ) => void;
    };

    /**
     * Entity dropdown config.
     */
    config?: EntityDropdownProps['config'];
};

const messages = defineMessages({
    entityCreationToastStart: {
        id: 'utterances.entityPaletteContainer.entityCreationToastStart',
        defaultMessage: 'Creating child entity ...'
    },
    entityCreationToastSuccess: {
        id: 'utterances.entityPaletteContainer.entityCreationToastSuccess',
        defaultMessage: 'Child entity created'
    },
    entityCreationToastFailed: {
        id: 'utterances.entityPaletteContainer.entityCreationToastFailed',
        defaultMessage: 'Create child entity failed'
    }
});

/**
 * Container component for the entity dropdown component.
 */
export const EntityDropdownContainer = observer((props: EntityDropdownContainerProps) => {
    const {
        entities,
        config,
        menuStore,
        tokenSelectionMetadata,
        onSetTokenSelectionMetadata,
        onOpenEntityPane,
        isEntityPaneOpen,
        utteranceLabels,
        actions: { onAddLabel, onAddSynonymToSublist, onConfirmPrediction, onCreateEntity, onCreateSublist, onRemoveLabels }
    } = props;
    const { appId, versionId } = useCurrentVersionedAppId();
    const { dispatcher } = useGlobalState();
    const history = useHistory();
    const { localize } = useLocalization();
    const [isPrebuiltEntityModalHidden, setIsPrebuiltEntityModalHidden] = React.useState(true);

    useDocumentEventHandler('wheel', (e: WheelEvent) => {
        const menuContainers = document.querySelectorAll('.ms-ContextualMenu-container');

        if ([...menuContainers].some(container => container.contains(e.target as Node))) {
            return;
        }

        menuStore.resetMenuState();
    });

    const labelsWithinSelection = tokenSelectionMetadata
        ? utteranceLabels.filter(
              label =>
                  !isMLPrediction(label) &&
                  ((label.start === tokenSelectionMetadata.selection[0] && label.end === tokenSelectionMetadata.selection[1]) ||
                      (label.start <= tokenSelectionMetadata.selection[0] && label.end >= tokenSelectionMetadata.selection[1]))
          )
        : [];

    const predictionsWithinSelection = tokenSelectionMetadata
        ? utteranceLabels.filter(
              label =>
                  isMLPrediction(label) &&
                  ((label.start === tokenSelectionMetadata.selection[0] && label.end === tokenSelectionMetadata.selection[1]) ||
                      (label.start >= tokenSelectionMetadata.selection[0] && label.end <= tokenSelectionMetadata.selection[1])) &&
                  label.entity?.typeId !== ModelType.List
          )
        : [];

    const doesSelectionHavePredictions = Boolean(predictionsWithinSelection.length);

    const isPrediction = menuStore.clickedOnLabel && isMLPrediction(menuStore.clickedOnLabel);

    const flattenedClickedOnLabel = menuStore.clickedOnLabel ? getAllTreeNodes<any>(menuStore.clickedOnLabel) : [];

    // If the user clicked on prediction, the entity dropdown will need its children
    // to perform the confirm sub-entities predictions action, otherwise it'll need the selected label
    // and its parents so that the nested dropdowns show the labeled entities from the root to the selected token.
    const selection = isPrediction ? flattenedClickedOnLabel.map(l => l.entity) : labelsWithinSelection.map(l => l.entity);

    const entitySelection = {
        selection,
        kind: isPrediction ? 'prediction' : 'label',
        clickedOnLabel: menuStore.clickedOnLabel
    } as EntityDropdownSelection;

    const handleDismiss = (e: React.MouseEvent<HTMLElement>) => {
        if (e?.type === 'click' && (e?.target as Element).hasAttribute(tokenDataAttribute)) {
            return;
        }

        menuStore.resetMenuState();
    };

    const handleOpenEntityPane = () => {
        transaction(() => {
            onSetTokenSelectionMetadata(null);
            menuStore.resetMenuState();
        });
        onOpenEntityPane();
    };

    const handleCreateChildEntity = (entityName: string, parentEntityId: LuisId) => {
        const absoluteParent = getAbsoluteParentById(parentEntityId, entities);
        if (absoluteParent) {
            dispatcher.dispatch('createSimpleChildEntity', {
                parentId: parentEntityId,
                absoluteParentId: absoluteParent.id,
                name: entityName,
                children: [],
                toastData: {
                    running: { title: localize(messages.entityCreationToastStart) },
                    completed: { title: localize(messages.entityCreationToastSuccess) },
                    failed: { title: localize(messages.entityCreationToastFailed) }
                }
            });
        }
    };

    const handleSubmitLabel = (entity: Entity) => {
        onAddLabel(
            menuStore.currentUtteranceId,
            {
                startTokenIndex: tokenSelectionMetadata.selection[0],
                endTokenIndex: tokenSelectionMetadata.selection[1],
                entityId: entity.id
            },
            'menu'
        );
    };

    const handleUnlabel = (unlabelEntities: Entity[]) => {
        const labelsToRemove = unlabelEntities
            .map(e =>
                labelsWithinSelection.find(label => {
                    const roleId = label.role?.id;
                    if (roleId) {
                        return label.entity?.id === e.id && e.roles.find(r => r.id === roleId);
                    }

                    return label.entity?.id === e.id;
                })
            )
            .map(label => ({
                startTokenIndex: label.start,
                endTokenIndex: label.end,
                entityId: label.entity?.id,
                roleId: label.role?.id
            }));

        onRemoveLabels(menuStore.currentUtteranceId, labelsToRemove);
    };

    const handleSublistCreation = (entityId: LuisId, sublistName: string) => {
        const value = tokenSelectionMetadata.selectedText;
        onCreateSublist(entityId, sublistName, [value]);
    };

    const handleSubmitSublist = (entity: ListEntity, sublistName: string) => {
        const value = tokenSelectionMetadata.selectedText;
        onAddSynonymToSublist(entity.id, sublistName, value);
    };

    const handleConfirmPrediction = (includeSubentities: boolean) => {
        if (menuStore.clickedOnLabel) {
            if (includeSubentities) {
                onConfirmPrediction(
                    menuStore.currentUtteranceId,
                    flattenedClickedOnLabel.map(l => ({
                        startTokenIndex: l.start,
                        endTokenIndex: l.end,
                        entityId: l.entity.id,
                        roleId: l.role?.id
                    })),
                    'predictionsMenuIncludeSubEntities'
                );
            } else {
                onConfirmPrediction(
                    menuStore.currentUtteranceId,
                    [
                        {
                            startTokenIndex: menuStore.clickedOnLabel.start,
                            endTokenIndex: menuStore.clickedOnLabel.end,
                            entityId: menuStore.clickedOnLabel.entity.id,
                            roleId: menuStore.clickedOnLabel.role?.id
                        }
                    ],
                    'predictionsMenu'
                );
            }
        } else {
            const mappedPredictionsWithinSelection = predictionsWithinSelection.map(l => ({
                startTokenIndex: l.start,
                endTokenIndex: l.end,
                entityId: l.entity.id,
                roleId: l.role?.id
            }));
            onConfirmPrediction(menuStore.currentUtteranceId, mappedPredictionsWithinSelection, 'selectionMenu');
        }
    };

    return (
        <>
            <EntityDropdown
                target={menuStore.target}
                hidden={menuStore.isHidden}
                havePredictions={doesSelectionHavePredictions}
                onSubmit={handleSubmitLabel}
                onUnLabel={handleUnlabel}
                onCreateSublist={handleSublistCreation}
                onSubmitSublist={handleSubmitSublist}
                entitySelection={entitySelection}
                confirmPrediction={handleConfirmPrediction}
                entities={entities}
                onCreate={onCreateEntity}
                onCreateChild={handleCreateChildEntity}
                onDismiss={handleDismiss}
                openPatternsPage={() => history.push(routeTable.luis.patternsPage(appId, versionId))}
                openEntityPane={handleOpenEntityPane}
                isEntityPaneOpen={isEntityPaneOpen}
                openPrebuiltEntityModal={() => setIsPrebuiltEntityModalHidden(false)}
                config={config}
            />
            <PrebuiltEntitiesModal
                hidden={isPrebuiltEntityModalHidden}
                onDismiss={() => setIsPrebuiltEntityModalHidden(true)}
                onSubmit={() => setIsPrebuiltEntityModalHidden(true)}
            />
        </>
    );
});
